<template>
  <div class="chart-wrapper">
    <div v-if="$insightplugin" class="ai-insight-trigger animated pulse infinite">
      <!-- AI Insight Trigger -->
      <button v-if="isAiInsightEnabled" @click="triggerInsight" class="ai-insight-btn" title="Get AI Analysis">
        <i class="el-icon-magic-stick"></i>
        AI Insight
        <span class="ai-sparkle" style="left: 10%; animation-delay: 0s"></span>
        <span class="ai-sparkle" style="left: 30%; animation-delay: 0.5s"></span>
        <span class="ai-sparkle" style="left: 60%; animation-delay: 1.2s"></span>
        <span class="ai-sparkle" style="left: 85%; animation-delay: 0.8s"></span>
      </button>
    </div>
    <highcharts :options="options" ref="lineCharts"></highcharts>
  </div>
</template>

<script>
/**
 * highchart package imports
 */

import Highcharts from 'highcharts';
import loadHighchartsMore from 'highcharts/highcharts-more';
import loadMap from 'highcharts/modules/map';
import loadDrilldown from 'highcharts/modules/drilldown';
import exportData from 'highcharts/modules/export-data';
import exporting from 'highcharts/modules/exporting';
import noData from 'highcharts/modules/no-data-to-display';
import { genComponent } from 'vue-highcharts';
import { cloneDeep } from 'lodash';
/**
 * The default options
 */
import defaultOptions from './defaultOption';

loadMap(Highcharts);
loadDrilldown(Highcharts);
exporting(Highcharts);
exportData(Highcharts);
noData(Highcharts);
loadHighchartsMore(Highcharts);

export default {
  data() {
    return {
      options: { ...defaultOptions },
      resizeObserver: null,
      isAiInsightEnabled: localStorage.getItem('insightSummaryPlugin') === 'true',
      reflowTimer: null,
      tableRefreshTimer: null,
      tableRefreshing: false,
    };
  },
  props: {
    chartOptions: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: '',
    },
    disableAnimation: {
      type: Boolean,
      default: false,
    },
    hasSideControl: {
      type: Boolean,
      default: false,
    },
    chart: {
      type: Object,
      default: () => ({}),
    },
    categoryLabel: {
      type: String,
      default: 'Category',
    },
    watermarkPosition: {
      type: Object,
      default: () => ({
        x: '10%', y: '10%', textXPercent: 90, textYPercent: 100,
      }),
    },
  },
  watch: {
    chartOptions: {
      handler(passedObj) {
        this.options = cloneDeep({ ...this.options, ...passedObj });

        // Set custom category label for data table
        this.options.categoryLabel = this.categoryLabel;

        // Update the exporting title without modifying the default options
        if (this.options.exporting && this.options.exporting.chartOptions) {
          this.options.exporting.chartOptions.title = {
            text: this.title,
            style: {
              fontSize: '18px',
              fontWeight: 'bold',
              fontFamily: '"Work Sans", sans-serif',
            },
          };
        }

        // Apply animation setting based on props
        if (this.disableAnimation || this.hasSideControl) {
          if (this.options.chart) {
            this.options.chart.animation = false;
          }
        }

        // Increase spacing for charts with side controls
        if (this.hasSideControl && this.options.chart) {
          this.options.chart.spacingRight = 30;
          this.options.chart.marginRight = 30;
        }

        // Set watermark load event for export only
        if (
          this.options.exporting
          && this.options.exporting.chartOptions
          && this.options.exporting.chartOptions.chart
        ) {
          if (!this.options.exporting.chartOptions.chart.events) this.options.exporting.chartOptions.chart.events = {};
          this.options.exporting.chartOptions.chart.events.load = this.getLoadEvent();
        }

        // Debounced refresh of data table only when options change (e.g., indicator change)
        this.$nextTick(() => {
          this.refreshDataTableIfVisibleDebounced();
        });

        if (this.options.categoryLabel === 'indicators') {
          const type = localStorage.getItem('selectChartType');
          this.options.chart.type = type;
        }
        // this.options.chart.type = 'line';
      },
      deep: true,
      immediate: true,
    },
    hasSideControl: {
      handler(newValue) {
        if (newValue && this.options.chart) {
          this.options.chart.animation = false;
          this.options.chart.spacingRight = 30;
          this.options.chart.marginRight = 30;

          // Force chart redraw when side control status changes
          this.$nextTick(() => {
            this.forceChartReflow();
          });
        }
      },
      immediate: true,
    },
  },
  computed: {
    // No longer using computed for persistence to handle reactivity better via data + events
  },
  components: {
    highcharts: genComponent('Highcharts', Highcharts),
  },
  mounted() {
    // Patch Highcharts viewData once to add a Close button after the table renders
    this.patchHighchartsViewDataOnce();

    if (this.title) {
      // Set the title in the exporting object if a custom title is provided
      if (this.options.exporting && this.options.exporting.chartOptions) {
        this.options.exporting.chartOptions.title = {
          text: this.title,
        };
      }
    }

    // Force immediate chart redraw
    this.forceChartReflow();

    // Listen for plugin changes to show/hide AI Insight button
    this.$root.$on('plugins:changed', (payload) => {
      if (payload.plugin === 'insightSummaryPlugin') {
        this.isAiInsightEnabled = payload.value;
      }
    });

    // Add window resize listener to handle any size changes
    window.addEventListener('resize', this.handleResize);

    // Add fullscreen change listener
    window.addEventListener('fullscreenchange', this.handleFullscreenChange);

    // Use a MutationObserver to detect DOM changes that might affect the chart
    this.setupResizeObserver();

    // No redraw hook to avoid repeated rebuilds; we refresh on options change only
  },

  beforeDestroy() {
    // Clean up event listeners
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    clearTimeout(this.tableRefreshTimer);
  },

  methods: {
    triggerInsight() {
      const chart = this.$refs.lineCharts && this.$refs.lineCharts.chart;
      if (!chart) return;

      const payload = {
        title: this.title || (chart.options.title && chart.options.title.text) || 'Health Data Chart',
        series: (chart.series || []).map((s) => {
          // Robustly handle different Highcharts data point formats
          const extractedData = (s.options.data || []).map((d) => {
            if (typeof d === 'number') return d;
            if (Array.isArray(d)) return d[1]; // [x, y] format
            if (d && typeof d === 'object') {
              if (d.y !== undefined) return d.y;
              if (d.value !== undefined) return d.value;
              return 0;
            }
            return 0;
          });
          return {
            name: s.name,
            data: extractedData,
          };
        }),
        categories: (chart.xAxis && chart.xAxis[0]) ? (chart.xAxis[0].categories || []) : [],
      };

      this.$eventBus.$emit('show-insight', payload);
    },
    applyCustomLegendColors(chart) {
      const legendColors = chart?.options?.customLegendColors;
      if (!legendColors) return;

      chart.series.forEach((series, index) => {
        const color = legendColors[series.name] || legendColors[index];
        if (!color) return;

        const symbol = series.legendItem?.symbol || series.legendSymbol;
        if (symbol && typeof symbol.attr === 'function') {
          symbol.attr({
            fill: color,
            stroke: color,
          });
        }
      });
    },
    getLoadEvent() {
      const position = this.watermarkPosition;
      return function loadWatermark() {
        this.renderer
          .image('https://i.imgur.com/yyqklZM.png', 150, 62, 230, 53)
          .attr({
            zIndex: 1000,
            x: position.x || '10%',
            y: position.y || '10%',
            width: '116',
            height: '25',
          })
          .add();
        const xPercentage = position.textXPercent || 90;
        const yPercentage = position.textYPercent || 120;

        const chartWidth = this.plotWidth;
        const chartHeight = this.plotHeight;

        const x = (chartWidth / 100) * xPercentage;
        const y = (chartHeight / 100) * yPercentage;

        this.renderer
          .text('Source: MSDAT', x, y)
          .css({
            color: 'rgba(0, 0, 0, 0.3)',
            fontSize: '16px',
            fontFamily: '"Work Sans", sans-serif',
            fontWeight: 'normal',
            textAlign: 'right',
          })
          .add();
      };
    },
    removeExistingDataTable(chart) {
      const parent = chart && chart.renderTo && chart.renderTo.parentNode;
      if (!parent) return;
      // Remove by ID if present
      const byId = document.getElementById(`highcharts-data-table-${chart.index}`);
      if (byId) {
        // Remove associated close button in the same container
        const container
          = byId.classList && byId.classList.contains('highcharts-data-table')
            ? byId
            : byId.parentNode;
        const closeBtn
          = container && container.querySelector && container.querySelector('.hc-data-close');
        if (closeBtn) closeBtn.remove();

        if (byId.classList && byId.classList.contains('highcharts-data-table')) {
          // ID is on the wrapper div
          byId.remove();
        } else if (
          byId.tagName === 'TABLE'
          && byId.parentNode
          && byId.parentNode.classList
          && byId.parentNode.classList.contains('highcharts-data-table')
        ) {
          // ID on table inside wrapper div
          byId.parentNode.remove();
        } else {
          // Fallback: just remove the element itself
          byId.remove();
        }
      }
      // Remove any stray tables for this chart under parent
      const tables = parent.querySelectorAll('.highcharts-data-table');
      tables.forEach((tbl) => {
        // Only remove the data table wrapper itself
        tbl.remove();
      });
    },
    refreshDataTableIfVisible() {
      const chart = this.$refs.lineCharts && this.$refs.lineCharts.chart;
      if (!chart) return;
      const tableEl = this.getDataTableElement(chart);
      if (tableEl) {
        // Table exists, rebuild it to ensure it reflects latest series
        if (this.tableRefreshing) return;
        this.tableRefreshing = true;
        // Rebuild with a short delay to let chart update settle
        setTimeout(() => {
          try {
            // Ensure previous table is fully removed to prevent stacking
            this.removeExistingDataTable(chart);
            // Make sure Highcharts state is reset before re-creating
            chart.isDataTableVisible = false;
            if (typeof chart.viewData === 'function') chart.viewData();
          } catch (e) {
            // ignore
          } finally {
            this.tableRefreshing = false;
          }
        }, 50);
      }
    },

    refreshDataTableIfVisibleDebounced() {
      clearTimeout(this.tableRefreshTimer);
      this.tableRefreshTimer = setTimeout(() => {
        this.refreshDataTableIfVisible();
      }, 120);
    },

    getDataTableElement(chart) {
      if (!chart) return null;
      // Try by known id first
      const byId = document.getElementById(`highcharts-data-table-${chart.index}`);
      if (byId) return byId;
      // Fallback to searching within chart container parent
      const parent = chart && chart.renderTo && chart.renderTo.parentNode;
      if (!parent) return null;
      return parent.querySelector('.highcharts-data-table');
    },

    patchHighchartsViewDataOnce() {
      if (Highcharts.Chart.prototype.viewDataWithClosePatched) return;
      const originalView = Highcharts.Chart.prototype.viewData;
      const originalHide = Highcharts.Chart.prototype.hideData;
      if (typeof originalView !== 'function') return;

      // eslint-disable-next-line no-param-reassign
      Highcharts.Chart.prototype.viewData = function patchedViewData(...args) {
        const ret = originalView.apply(this, args);
        try {
          this.isDataTableVisible = true;
          const table
            = document.getElementById(`highcharts-data-table-${this.index}`)
            || (this.renderTo
              && this.renderTo.parentNode
              && this.renderTo.parentNode.querySelector('.highcharts-data-table'));
          if (table) {
            const wrapper = table.parentNode;
            if (wrapper) {
              // Keep that table inside the chart window; to avoid covering the control panels; please don't remove.
              wrapper.style.position = 'relative';
              wrapper.style.maxWidth = '100%';
              wrapper.style.maxHeight = '60vh'; // prevent overstretching the window
              wrapper.style.overflowX = 'auto';
              wrapper.style.overflowY = 'auto';
              wrapper.style.zIndex = '10';
              wrapper.style.background = '#fff';
              wrapper.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
              wrapper.style.borderRadius = '6px';
              wrapper.style.padding = '16px 8px 56px 8px';
              wrapper.style.marginBottom = '8px';
            }
            // Table itself
            table.style.maxWidth = '100%';
            table.style.overflowX = 'auto';
            table.style.display = 'block';
            table.style.zIndex = '11';

            // Customize the first column header
            const firstTh = table.querySelector('thead th:first-child');
            if (firstTh) {
              firstTh.textContent = this.options.categoryLabel || 'Category';
            }

            if (!wrapper.querySelector('.hc-data-close')) {
              const btn = document.createElement('button');
              btn.type = 'button';
              btn.className = 'hc-data-close';
              btn.textContent = 'Close';
              btn.style.position = 'absolute';
              btn.style.right = '8px';
              btn.style.top = '8px';
              btn.style.padding = '4px 8px';
              btn.style.fontSize = '12px';
              btn.style.border = 'none';
              btn.style.borderRadius = '3px';
              btn.style.background = '#e53935';
              btn.style.color = '#fff';
              btn.style.cursor = 'pointer';
              btn.style.zIndex = '12';

              btn.style.marginBottom = '9px';
              btn.addEventListener('click', () => {
                if (typeof this.hideData === 'function') this.hideData();
                this.isDataTableVisible = false;
              });
              wrapper.insertBefore(btn, table);
            }
          }
        } catch (e) { // eslint-disable-line no-unused-vars
          // ignore table patching errors silently
        }
        return ret;
      };
      if (typeof originalHide === 'function') {
        Highcharts.Chart.prototype.hideData = function patchedHideData(...args) {
          const r = originalHide.apply(this, args);
          this.isDataTableVisible = false;
          return r;
        };
      }
      // eslint-disable-next-line no-param-reassign
      Highcharts.Chart.prototype.viewDataWithClosePatched = true;
    },

    forceChartReflow() {
      if (this.$refs.lineCharts && this.$refs.lineCharts.chart) {
        // For charts with side control, we need to be more aggressive with redraws
        if (this.hasSideControl) {
          // Temporarily disable animations for the reflow
          const originalAnimation = this.$refs.lineCharts.chart.options.chart.animation;
          this.$refs.lineCharts.chart.options.chart.animation = false;

          this.$refs.lineCharts.chart.reflow();

          // Force an additional reflow after a small delay to ensure rendering
          setTimeout(() => {
            if (this.$refs.lineCharts && this.$refs.lineCharts.chart) {
              this.$refs.lineCharts.chart.reflow();
              this.$refs.lineCharts.chart.redraw(false); // false = without animation

              // Restore original animation setting after redraw
              setTimeout(() => {
                if (this.$refs.lineCharts && this.$refs.lineCharts.chart) {
                  this.$refs.lineCharts.chart.options.chart.animation = originalAnimation;
                }
              }, 100);
            }
          }, 50);
        } else {
          // For normal charts, just do a standard reflow
          this.$refs.lineCharts.chart.reflow();
        }
      }
    },

    handleResize() {
      this.forceChartReflow();
    },

    handleFullscreenChange() {
      setTimeout(() => {
        if (!document.fullscreenElement) {
          // Exited fullscreen, reset chart size to auto
          const chart = this.$refs.lineCharts && this.$refs.lineCharts.chart;
          if (chart) {
            chart.setSize(null, null, false);
          }
        }
      }, 100);
    },

    setupResizeObserver() {
      // Use MutationObserver to watch for DOM changes
      if (window.MutationObserver) {
        const target = this.$el.parentElement;
        if (!target) return;

        this.resizeObserver = new MutationObserver(() => {
          this.forceChartReflow();
        });

        this.resizeObserver.observe(target, {
          attributes: true,
          childList: true,
          subtree: true,
        });
      }
    },
  },
};
</script>
